import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { getTournaments, getActiveTournaments, getMyTournaments } from '../api/tournaments';
import HrnbButton from "../components/controls/HrnbButton";
import HrnbAlert from "../components/controls/HrnbAlert";
import HrnbLoader from "../components/controls/HrnbLoader";
import { HrnbTabs, HrnbTab } from '../components/controls/HrnbTabs';
import { PlusIcon, BackIcon, ForwardIcon, TrophyIcon } from '../icons';
import TournamentCard from '../components/duels/TournamentCard';
import '../css/pages/TournamentsPage.css';

/**
 * Страница списка турниров
 */
function TournamentsPage() {
    const { currentUser } = useContext(UserContext);
    const [tournaments, setTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('active');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const size = 10;

    // Загрузка турниров
    useEffect(() => {
        const loadTournaments = async () => {
            setLoading(true);
            setError(null);

            try {
                let response;
                if (activeTab === 'active') {
                    response = await getActiveTournaments({ page, size });
                } else if (activeTab === 'my') {
                    response = await getMyTournaments({ page, size });
                } else {
                    response = await getTournaments({ page, size });
                }

                setTournaments(response.tournaments || []);
                setTotalPages(response.totalPages || 0);
            } catch (err) {
                console.error('Ошибка при загрузке турниров:', err);
                setError('Не удалось загрузить турниры. Попробуйте позже.');
            } finally {
                setLoading(false);
            }
        };

        loadTournaments();
    }, [activeTab, page]);

    // Обработчик переключения вкладок
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPage(0);
    };

    // Обработчик пагинации
    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setPage(newPage);
        }
    };

    // Статусы
    const statusLabels = {
        REGISTRATION: 'Регистрация',
        UPCOMING: 'Скоро начнётся',
        ACTIVE: 'Активен',
        FINISHED: 'Завершён',
        CANCELLED: 'Отменён'
    };

    const statusColors = {
        REGISTRATION: 'success',
        UPCOMING: 'info',
        ACTIVE: 'primary',
        FINISHED: 'secondary',
        CANCELLED: 'danger'
    };

    return (
        <div className="tournaments-page">
            <div className="tournaments-page__header">
                <h1 className="tournaments-page__title">Турниры</h1>
                {currentUser && (
                    <Link to="/tournaments/create">
                        <HrnbButton variant="primary" icon={<PlusIcon />}>
                            Создать турнир
                        </HrnbButton>
                    </Link>
                )}
            </div>

            <HrnbTabs value={activeTab} onChange={handleTabChange}>
                <HrnbTab label="Активные" value="active" />
                <HrnbTab label="Все" value="all" />
                {currentUser && <HrnbTab label="Мои" value="my" />}
            </HrnbTabs>

            {error && (
                <HrnbAlert variant="danger" onClose={() => setError(null)}>
                    {error}
                </HrnbAlert>
            )}

            {loading ? (
                <HrnbLoader />
            ) : tournaments.length === 0 ? (
                <div className="tournaments-page__empty">
                    <p>
                        {activeTab === 'active'
                            ? 'Нет активных турниров'
                            : activeTab === 'my'
                            ? 'Вы пока не участвуете в турнирах'
                            : 'Турниров пока нет'
                        }
                    </p>
                    {currentUser && activeTab !== 'my' && (
                        <Link to="/tournaments/create">
                            <HrnbButton variant="primary" icon={<TrophyIcon />}>
                                Создать первый турнир
                            </HrnbButton>
                        </Link>
                    )}
                </div>
            ) : (
                <>
                    <div className="tournaments-page__list">
                        {tournaments.map((tournament) => (
                            <TournamentCard key={tournament.id} tournament={tournament} />
                        ))}
                    </div>

                    {totalPages > 1 && (
                        <div className="tournaments-page__pagination">
                            <HrnbButton
                                variant="secondary"
                                disabled={page === 0}
                                onClick={() => handlePageChange(page - 1)}
                                icon={<BackIcon />}
                            >
                                Назад
                            </HrnbButton>

                            <span className="tournaments-page__page-info">
                                Страница {page + 1} из {totalPages}
                            </span>

                            <HrnbButton
                                variant="secondary"
                                disabled={page >= totalPages - 1}
                                onClick={() => handlePageChange(page + 1)}
                                icon={<ForwardIcon />}
                            >
                                Вперёд
                            </HrnbButton>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default TournamentsPage;
